/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 선택영역(Range 원시 연산 + 캐럿-오프셋 변환)을 정의한다.
 *
 * @file /src/rootin/selection/Selection.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 27.
 */
import { Editor } from '@rootin/core/Editor';
import { Element } from '@rootin/base/Element';
import { Block } from '@rootin/base/Block';
import { Html } from '@utils/Html';

/**
 * 선택영역 반환 타입
 */
export type SelectionState = { kind: 'range'; range: Range } | { kind: 'none' };

export class Selection {
    /**
     * 에디터의 선택영역(Range 원시 연산 + 캐럿-오프셋 변환)을 정의한다.
     *
     * @param editor - 선택영역을 계산할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 현재 선택 상태를 가져온다.
     *
     * @returns 현재 선택 상태
     */
    get(): SelectionState {
        const range = this.getRange();
        if (range !== null) {
            return { kind: 'range', range };
        }

        return { kind: 'none' };
    }

    /**
     * 현재 선택영역을 가져온다.
     *
     * @returns 에디터 내부의 Range (그 밖이거나 선택영역 자체가 없으면 null)
     */
    getRange(): Range | null {
        const selection = document.getSelection();
        if (selection === null || selection.rangeCount === 0) {
            return null;
        }

        const range = selection.getRangeAt(0);
        if (this.editor.root.body.$getElement().getEl().contains(range.commonAncestorContainer) === false) {
            return null;
        }

        return range;
    }

    /**
     * 현재 선택된 텍스트를 문자열로 가져온다.
     *
     * @returns 선택된 텍스트
     */
    getText(): string {
        const state = this.get();
        return state.kind === 'range' ? state.range.toString() : '';
    }

    /**
     * 특정 영역을 선택한다.
     *
     * @param range - 적용할 Range
     */
    setRange(range: Range): void {
        const selection = document.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
    }

    /**
     * 현재 선택영역을 엘리먼트 기준 문자 오프셋으로 변환한다.
     *
     * @param element - 기준 엘리먼트
     * @returns 선택영역의 시작/끝 문자 오프셋 (선택영역 자체가 없으면 null)
     */
    getOffsets(element: Element): { start: number; end: number } | null {
        const range = this.getRange();
        if (range === null) {
            return null;
        }

        return {
            start: this.getTextLength(element, range.startContainer, range.startOffset),
            end: this.getTextLength(element, range.endContainer, range.endOffset),
        };
    }

    /**
     * 문자 오프셋으로 선택영역을 지정한다.
     *
     * @param element - 기준 엘리먼트
     * @param start - 시작 문자 오프셋
     * @param end - 끝 문자 오프셋
     */
    setOffsets(element: Element, start: number, end: number): void {
        const el = element.$getElement().getEl();
        const s = this.getTextOffset(element, start, 'start');
        const e = this.getTextOffset(element, end, 'end');

        const range = document.createRange();
        if (s !== null) {
            range.setStart(s.node, s.offset);
        } else {
            range.setStart(el, el.childNodes.length);
        }
        if (e !== null) {
            range.setEnd(e.node, e.offset);
        } else {
            range.setEnd(el, el.childNodes.length);
        }

        this.setRange(range);
    }

    /**
     * 엘리먼트 처음부터 문자 오프셋이 가리키는 DOM 위치까지의 문자수를 센다.
     *
     * @param element - 기준 엘리먼트
     * @param container - 경계 컨테이너 노드
     * @param offset - 경계 오프셋 (`container`가 텍스트 노드면 문자 단위, 엘리먼트면 자식 인덱스)
     * @returns `element` 시작부터 경계까지의 문자수
     */
    getTextLength(element: Element, container: Node, offset: number): number {
        const el = element.$getElement().getEl();
        const range = document.createRange();
        range.setStart(el, 0);
        range.setEnd(container, offset);
        return range.toString().length;
    }

    /**
     * 엘리먼트의 텍스트를 기준으로 타겟위치에 해당하는 실제 DOM 위치를 가져온다.
     *
     * @param element - 기준 엘리먼트
     * @param target - 찾을 문자 위치
     * @param bias - 경계에 정확히 걸칠 때 'start'면 다음 노드 앞으로, 'end'면 현재 노드 끝으로
     * @returns 해당 텍스트노드와 그 안의 DOM 오프셋 (`target`이 전체 텍스트 길이를 넘으면 null)
     */
    getTextOffset(
        element: Element,
        target: number,
        bias: 'start' | 'end' = 'end'
    ): { node: Node; offset: number } | null {
        const el = element.$getElement().getEl();
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let count = 0;
        let node = walker.nextNode();
        let last: Node | null = null;
        while (node !== null) {
            const length = node.textContent?.length ?? 0;
            const reached = bias === 'start' ? count + length > target : count + length >= target;
            if (reached === true) {
                return { node, offset: target - count };
            }
            count += length;
            last = node;
            node = walker.nextNode();
        }

        // bias가 'start'여도 target이 정확히 전체 텍스트 끝이면(다음 노드가 없으면) 마지막
        // 텍스트노드의 끝으로 떨어져야 한다 — 그렇지 않으면 엘리먼트 맨 끝의 오프셋이 항상
        // null을 반환해버린다.
        if (last !== null && target === count) {
            return { node: last, offset: last.textContent?.length ?? 0 };
        }

        return null;
    }

    /**
     * 잠긴 블록 목록을 DOM에서 그때그때 가져온다.
     *
     * @returns 잠긴 블록 목록(문서 순서)
     */
    getSelectedBlocks(): Block[] {
        return Html.all('rootin-block[data-selected=true]', this.editor.root.body.$getElement())
            .getList()
            .map(($el) => $el.getData('block') as Block | undefined)
            .filter((block): block is Block => block != null);
    }

    /**
     * 캐럿이 실제로 들어갈 수 있는 모든 지점의 엘리먼트를 가져온다.
     *
     * @returns 문서 순서대로 나열된 엘리먼트 목록
     */
    getElements(): Element[] {
        return Html.all('rootin-element[contenteditable=true]', this.editor.root.body.$getElement())
            .getList()
            .map(($el) => $el.getData('element') as Element | undefined)
            .filter((element): element is Element => element != null);
    }

    /**
     * 현재 선택영역이 블록파괴가 일어날 수 있는지 확인한다 — SR의 시작/끝이 같은 Element 하나
     * 안에 있지 않으면(=Element 경계를 넘나들면) 무조건 파괴적이다.
     *
     * **"첫 블록이 atomic일 때만 위험하다"는 예전 판정은 더 이상 못 쓴다** — 그 판정은 블록을
     * 잠글 때 SR을 그 블록의 부모 기준 전/후 경계로 정확히 스냅한다는 전제 위에서 실측 검증된
     * 것이었는데, 지금은 그 스냅을 하지 않는다(`SelectionChange`가 캐럿을 블록 안쪽 첫/끝
     * 지점에만 놓는다 — 복사/붙여넣기 정밀 스냅은 나중에 별도 구현 예정). 그래서 SR의 실제
     * 모양이 그 실측 전제와 달라져, 예전 규칙을 믿을 수 없다.
     *
     * **Element 경계를 넘나드는지로 판정을 바꾸면 부수적으로 히스토리 버그도 같이 고쳐진다** —
     * atomic 블록이 하나도 안 낀 순수 여러 Element에 걸친 삭제(예: 문단 두 개 사이)는 예전
     * 판정으로는 위험하지 않다고 봐서 네이티브가 그대로 처리했는데, 네이티브 삭제는 우리
     * `Container.onBeforeEdit()`(히스토리 스냅샷)를 아예 거치지 않아 그 삭제가 undo 복원이
     * 안 됐다. 이제는 이 경우도 파괴적으로 판정돼 우리 쪽 처리(`Events.#removeSelectedBlocks()`)를
     * 타므로, 그 안에서 `onBeforeEdit()`을 직접 불러 히스토리도 정상 기록된다. 반대로 SR이
     * 하나의 Element 안에만 있으면(Element 경계를 안 넘으면) 어차피 그 Element 자신의 통상적인
     * 입력 처리 경로에서 히스토리가 기록되므로 그대로 네이티브에 맡겨도 된다.
     *
     * @returns 네이티브 편집(삭제/타이핑)을 그대로 두면 안 되는 상태인지
     */
    isDestructiveSelection(): boolean {
        const range = this.getRange();
        if (range === null) {
            return false;
        }

        const startElement = this.editor.focus.resolveElement(range.startContainer);
        const endElement = this.editor.focus.resolveElement(range.endContainer);

        return startElement === null || endElement === null || startElement !== endElement;
    }

    /**
     * 두 네이티브 노드 사이를 선택한다.
     *
     * @param $from - 선택 시작 노드(이 노드 전체가 선택 범위에 포함됨)
     * @param $to - 선택 종료 노드(이 노드 전체가 선택 범위에 포함됨)
     */
    select($from: Node, $to: Node): void {
        const range = document.createRange();
        range.setStartBefore($from);
        range.setEndAfter($to);
        this.setRange(range);
    }

    /**
     * 제스처가 시작되는 바로 그 순간의 native anchor를(element+문자오프셋으로) 캡처한다.
     *
     * @param native - 기준 네이티브 Selection
     * @returns 캡처된 anchor 위치 (해석 불가능하면 `null`)
     */
    captureAnchor(native: globalThis.Selection | null): { element: Element; offset: number } | null {
        if (native === null || native.anchorNode === null) {
            return null;
        }

        const element = this.editor.focus.resolveElement(native.anchorNode);
        if (element === null) {
            return null;
        }

        return { element, offset: this.getTextLength(element, native.anchorNode, native.anchorOffset) };
    }
}
